// components/CreatorsPanel/CreatorsPanel.tsx — the shared "creators rail with a
// working Follow" right-rail panel. Introduced in slice-4-xl's Live fix round
// (S4): Videos' TopCreatorsPanel and Live's TopLiveCreatorsPanel were
// byte-identical apart from their title — this is the one implementation,
// parameterized by `title`. Both feature panels are now thin wrappers around
// this component (see src/features/videos/panels/TopCreatorsPanel.tsx and
// src/features/live/panels/TopLiveCreatorsPanel.tsx) so import sites don't
// need to change.
import styled from 'styled-components';
import { Panel, SectionLabel, Avatar, Button, TierBadge, deriveTier } from '@/ui';
import { useFollow, useRequireAuth, followButtonLabel } from '@/hooks';
import { formatCount } from '@/components/formatCount';
import type { Creator } from '@/core/models/creator';

const List = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const Row = styled.li`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Info = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 1px;
`;

const NameLine = styled.span`
  display: flex;
  align-items: center;
  gap: 5px;
  min-width: 0;
`;

const Name = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  min-width: 0;
  flex: 0 1 auto;
`;

const Followers = styled.span`
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.xs};
  color: ${({ theme }) => theme.color.textMuted};
`;

function CreatorRow({ creator }: { creator: Creator }) {
  const { isFollowing, isRequested, toggleFollow } = useFollow(creator.handle);
  const requireAuth = useRequireAuth();

  function handleFollow() {
    if (!requireAuth('follow')) return;
    void toggleFollow();
  }

  return (
    <Row>
      <Avatar name={creator.name} avatarKey={creator.av} size={36} />
      <Info>
        <NameLine>
          <Name>{creator.name}</Name>
          <TierBadge tier={deriveTier(creator.verified)} />
        </NameLine>
        <Followers>{formatCount(creator.followerCount)} followers</Followers>
      </Info>
      <Button
        variant={isFollowing || isRequested ? 'secondary' : 'outline'}
        size="sm"
        onClick={handleFollow}
        aria-label={`${followButtonLabel(isFollowing, isRequested)} ${creator.handle}`}
      >
        {followButtonLabel(isFollowing, isRequested)}
      </Button>
    </Row>
  );
}

export interface CreatorsPanelProps {
  /** Panel heading, e.g. "Top creators" (Videos) or "Top Live Creators" (Live). */
  title: string;
  creators: Creator[];
}

export function CreatorsPanel({ title, creators }: CreatorsPanelProps) {
  return (
    <Panel>
      <SectionLabel as="h2">{title}</SectionLabel>
      <List>
        {creators.map((c) => (
          <CreatorRow key={c.id} creator={c} />
        ))}
      </List>
    </Panel>
  );
}
